import styled from 'styled-components';
import { motion } from 'framer-motion';

export const NotificationPanel = styled(motion.div)`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 0.5rem;
  background-color: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  box-shadow: var(--shadow-lg);
  width: 360px;
  max-height: 500px;
  z-index: 1000;
  overflow: hidden;

  @media (max-width: 768px) {
    right: -1rem;
    width: 320px;
    max-height: 400px;
  }

  @media (max-width: 480px) {
    width: 280px;
    right: -2rem;
  }
`;

export const PanelHeader = styled.div`
  padding: 1rem;
  border-bottom: 1px solid var(--border-color);
  background-color: var(--background-secondary);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const PanelTitle = styled.h3`
  color: var(--text-primary);
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const MarkAllButton = styled.button`
  background: none;
  border: none;
  color: var(--primary-color);
  font-size: 0.875rem;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  border-radius: 0;
  transition: var(--transition);
  display: flex;
  align-items: center;
  gap: 0.25rem;

  &:hover {
    background-color: var(--background-tertiary);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 0;
  transition: var(--transition);

  &:hover {
    background-color: var(--background-tertiary);
    color: var(--text-primary);
  }
`;

export const NotificationList = styled.div`
  max-height: 400px;
  overflow-y: auto;

  @media (max-width: 768px) {
    max-height: 300px;
  }
`;

export const NotificationItem = styled(motion.div)`
  padding: 1rem;
  border-bottom: 1px solid var(--border-color);
  cursor: pointer;
  transition: var(--transition);
  position: relative;

  &:hover {
    background-color: var(--background-secondary);
  }

  &:last-child {
    border-bottom: none;
  }

  &.unread {
    background-color: rgba(216, 184, 137, 0.05);
    border-left: 3px solid var(--primary-color);
  }
`;

export const NotificationIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.75rem;
  flex-shrink: 0;

  &.info {
    background-color: rgba(96, 165, 250, 0.1);
    color: var(--info-color);
  }

  &.success {
    background-color: rgba(104, 211, 145, 0.1);
    color: var(--success-color);
  }

  &.warning {
    background-color: rgba(246, 224, 94, 0.1);
    color: var(--warning-color);
  }

  &.error {
    background-color: rgba(252, 129, 129, 0.1);
    color: var(--error-color);
  }
`;

export const NotificationContent = styled.div`
  flex: 1;
  min-width: 0;
`;

export const NotificationTitle = styled.h4`
  color: var(--text-primary);
  font-size: 0.875rem;
  font-weight: 600;
  margin: 0 0 0.25rem 0;
  line-height: 1.3;
`;

export const NotificationMessage = styled.p`
  color: var(--text-secondary);
  font-size: 0.8rem;
  margin: 0 0 0.5rem 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const NotificationTime = styled.span`
  color: var(--text-muted);
  font-size: 0.75rem;
`;

export const NotificationActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

export const ActionButton = styled.button`
  background: none;
  border: 1px solid var(--border-color);
  color: var(--text-secondary);
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  border-radius: 0;
  cursor: pointer;
  transition: var(--transition);
  display: flex;
  align-items: center;
  gap: 0.25rem;

  &:hover {
    background-color: var(--background-tertiary);
    color: var(--text-primary);
  }

  &.primary {
    background-color: var(--primary-color);
    color: var(--text-inverse);
    border-color: var(--primary-color);

    &:hover {
      background-color: var(--primary-dark);
    }
  }
`;

export const EmptyState = styled.div`
  padding: 2rem;
  text-align: center;
  color: var(--text-muted);

  svg {
    width: 48px;
    height: 48px;
    margin-bottom: 1rem;
    opacity: 0.5;
  }

  p {
    margin: 0;
    font-size: 0.875rem;
  }
`;

export const LoadingState = styled.div`
  padding: 2rem;
  text-align: center;
  color: var(--text-muted);

  p {
    margin: 0;
    font-size: 0.875rem;
  }
`;

export const UnreadIndicator = styled.div`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  width: 8px;
  height: 8px;
  background-color: var(--primary-color);
  border-radius: 0;
`;
